/* ==========================================================================
   Keeping the app the size of the space it actually has

   A tester put it well: "the screen floats around, such as after writing text
   in this form." That is the on-screen keyboard. On Android the layout can be
   told to resize; on iOS it cannot, and the page ends up taller than the room
   left above the keyboard, so it drifts.

   VisualViewport reports the space that is genuinely visible. We publish it as
   a CSS variable and size the app to that instead of to the screen.
   ========================================================================== */

export function trackViewport(): void {
  const vv = window.visualViewport;

  /* Below this, a difference between the window and the visible area is the
     keyboard. Above it, it is rounding, a scrollbar, or an inset -- and
     writing that into --app-height is what left the app standing a finger's
     width short of the bottom of an iPhone, with the page colour showing
     under the tab bar. */
  const KEYBOARD = 120;

  /* A keyboard is open because somebody is typing into something. The size
     difference alone is a guess, and a guess made once at launch is how the
     app ended up standing short of the bottom of an installed iPhone app:
     iOS can report an odd height for a moment while the thing is still
     opening, that one bad number gets written into --app-height, and if no
     further viewport event ever fires there is nothing to correct it.

     Asking what has focus cannot misfire that way. No field, no override. */
  const typing = (): boolean => {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  };

  const apply = () => {
    const layout = window.innerHeight;
    const visible = vv ? vv.height : layout;


    /* Only claim a height when there is a keyboard to make room for.
       Otherwise the shell asks the browser for the window's height itself,
       which it knows better than we do. */
    const keyboard = typing() && layout - visible > KEYBOARD;
    if (keyboard) {
      document.documentElement.style.setProperty('--app-height', visible + 'px');
    } else {
      document.documentElement.style.removeProperty('--app-height');
    }
    /* Said out loud, so the stylesheet can put the tab bar away rather than
       leaving it between the message box and the keyboard. */
    document.documentElement.classList.toggle('keyboard', keyboard);

    /* iOS scrolls the *page* to reveal a focused field, which slides our fixed
       shell out of view -- the app bar disappears off the top and the chat
       sits in the wrong place until the keyboard goes. Nothing here should
       ever scroll but the screen body, so the page is put back whenever it
       has moved. (This used to check for offsetTop === 0 and then scroll to
       zero -- a reset that only ran when there was nothing to reset.) */
    if (vv && (vv.offsetTop !== 0 || window.scrollY !== 0)) window.scrollTo(0, 0);
  };

  apply();

  if (vv) {
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
  }
  window.addEventListener('orientationchange', () => setTimeout(apply, 120));
  window.addEventListener('resize', apply);

  /* The keyboard going away does not always come with a resize worth acting
     on, and a field losing focus is the plainest signal that it has. */
  document.addEventListener('focusin', apply);
  document.addEventListener('focusout', () => setTimeout(apply, 60));

  /* An installed app is suspended and resumed rather than reloaded, and it
     can come back to a different window than it left -- rotated, or with the
     keyboard gone. Both of these are the moments a stale height would
     otherwise survive for the whole of the next session. */
  window.addEventListener('pageshow', apply);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) apply();
  });

  /* And once more after everything has settled, because the measurement that
     matters least is the one taken while the app is still opening. */
  window.addEventListener('load', () => setTimeout(apply, 200));
}
